import React, { useState, useCallback, useRef, useEffect } from 'react';
import api from '../../services/api';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled
} from '@mui/material';
import { useData } from '../../contexts/DataContext';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SyncIcon from '@mui/icons-material/Sync';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { TablePagination } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { alpha } from '@mui/material/styles';
import SectionHeader from '../common/SectionHeader';
import AppDialog from '../common/AppDialog';
import AccountMappingDialog from './AccountMappingDialog';
import { accountMatchesExternalId, isAkahuId } from '../../utils/categoryAccountUtils';

// Styled components for modern design
const ImportCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
  borderRadius: theme.shape.borderRadius * 1.5,
  overflow: 'visible',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
  }
}));

// ImportHeader replaced by SectionHeader

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  textTransform: 'none',
  fontWeight: 500,
  boxShadow: 'none',
  padding: '8px 24px',
  [theme.breakpoints.down('sm')]: {
    padding: '6px 16px',
    fontSize: '0.875rem',
  }
}));

// Page sizes matching the Transactions page list
const ROWS_OPTIONS = [25, 50, 100];

// Storage key for saved unknown-account mappings (CSV import)
const ACCOUNT_MAPPINGS_KEY = 'budgetClues_accountMappings';

// "2 hours ago" style label without extra dependencies
const timeAgo = (date) => {
  if (!date) return null;
  const mins = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const CsvImportTool = () => {
  const { importTransactionsFromCSV, accounts, fetchAccounts, fetchTransactions, addAccount } = useData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Expanded method for accordion behavior ('api' | 'csv' | null)
  const [expandedMethod, setExpandedMethod] = useState('api');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0, step: 0 });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [importStats, setImportStats] = useState({ new: 0, updated: 0, failed: 0 });
  const importStartTimeRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = parseInt(localStorage.getItem('import_rowsPerPage') || '50', 10);
    return ROWS_OPTIONS.includes(stored) ? stored : 50;
  });

  // Automatic bank sync status ({ inProgress, lastSync }) and the manual
  // Sync Now action state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Account mapping dialog state (CSV imports with unknown account values)
  const [showAccountMappingDialog, setShowAccountMappingDialog] = useState(false);
  const [unknownAccountsList, setUnknownAccountsList] = useState([]);
  const [autoResolvedCount, setAutoResolvedCount] = useState(0);
  const [pendingImportData, setPendingImportData] = useState(null); // { transactions, latestAccounts, source: 'csv' }

  const [mappings, setMappings] = useState({
    date: '',
    description: '',
    amount: '',
    account: '',
    balance: ''
  });

  const resetState = () => {
    setFile(null);
    setResult(null);
    setPreview([]);
    setHeaders([]);
    setProgress({ current: 0, total: 0, percentage: 0 });
  };

  // --- Saved account mappings (localStorage) ---
  const loadSavedAccountMappings = () => {
    try {
      const stored = localStorage.getItem(ACCOUNT_MAPPINGS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const saveSavedAccountMappings = (mappingsObj) => {
    try {
      localStorage.setItem(ACCOUNT_MAPPINGS_KEY, JSON.stringify(mappingsObj));
    } catch (err) {
      console.error('Error saving account mappings:', err);
    }
  };

  // Detect unknown accounts in a list of transactions, auto-resolve from saved mappings
  const detectUnknownAccounts = (transactions, existingAccounts) => {
    const savedMappings = loadSavedAccountMappings();
    const accountCounts = {}; // { unknownName: transactionCount }
    const resolvedTransactions = [...transactions];
    let autoResolved = 0;

    const findExistingAccount = (accountValue) => {
      return existingAccounts.find(a =>
        accountMatchesExternalId(a, accountValue) || // internal id or linked (Akahu) id
        a.name === accountValue ||
        a.name.trim() === accountValue.trim() ||
        a.name.toLowerCase() === accountValue.toLowerCase() ||
        a.name.toLowerCase().trim() === accountValue.toLowerCase().trim()
      );
    };

    for (let i = 0; i < resolvedTransactions.length; i++) {
      const txn = resolvedTransactions[i];
      if (!txn.account) continue;

      const existing = findExistingAccount(txn.account);
      if (existing) {
        // Already matches an existing account — resolve to its ID
        resolvedTransactions[i] = { ...txn, account: existing.id };
        continue;
      }

      // Check saved mappings
      const saved = savedMappings[txn.account];
      if (saved && saved.targetAccountId) {
        const targetStillExists = existingAccounts.find(a => a.id === saved.targetAccountId);
        if (targetStillExists) {
          resolvedTransactions[i] = { ...txn, account: targetStillExists.id };
          autoResolved++;
          continue;
        }
        // Stale mapping — remove it
        delete savedMappings[txn.account];
        saveSavedAccountMappings(savedMappings);
      }

      // Truly unknown
      accountCounts[txn.account] = (accountCounts[txn.account] || 0) + 1;
    }

    const unknownAccounts = Object.entries(accountCounts).map(([name, transactionCount]) => ({
      name,
      transactionCount,
      displayName: null,
    }));

    return { unknownAccounts, resolvedTransactions, autoResolved };
  };

  // Apply user's mapping decisions: create new accounts or remap transaction account fields
  const applyAccountMappings = async (transactions, mappingDecisions, existingAccounts) => {
    const updatedTransactions = [...transactions];
    const newSavedMappings = loadSavedAccountMappings();

    for (const [unknownName, decision] of Object.entries(mappingDecisions)) {
      if (decision.action === 'MAP') {
        // Remap all transactions with this account name to the target ID
        for (let i = 0; i < updatedTransactions.length; i++) {
          if (updatedTransactions[i].account === unknownName) {
            updatedTransactions[i] = { ...updatedTransactions[i], account: decision.targetId };
          }
        }
        // Persist if "remember" is checked
        if (decision.remember) {
          newSavedMappings[unknownName] = {
            targetAccountId: decision.targetId,
            targetAccountName: decision.targetName || '',
            savedAt: new Date().toISOString(),
          };
        }
      } else if (decision.action === 'CREATE_NEW') {
        // Pre-create the account with user-chosen type. If the unknown value is a
        // bank (Akahu) account id, record the link so the account shows as linked
        // and future imports file transactions against it.
        const newAccount = {
          name: unknownName,
          type: decision.type || 'checking',
          isActive: true,
          linkedAccounts: isAkahuId(unknownName) ? [unknownName] : [],
        };
        try {
          const created = await addAccount(newAccount);
          const newId = created.id || created._id;
          for (let i = 0; i < updatedTransactions.length; i++) {
            if (updatedTransactions[i].account === unknownName) {
              updatedTransactions[i] = { ...updatedTransactions[i], account: newId };
            }
          }
        } catch (err) {
          console.error(`Error creating account "${unknownName}":`, err);
        }
      }
    }

    saveSavedAccountMappings(newSavedMappings);
    return updatedTransactions;
  };

  // Load the automatic-sync status on mount
  const loadSyncStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/akahu/sync/status');
      setSyncStatus(data);
      return data;
    } catch (error) {
      // Optional — e.g. the bank isn't connected
      return null;
    }
  }, []);

  useEffect(() => {
    loadSyncStatus();
  }, [loadSyncStatus]);

  // While a background sync is running, keep the status fresh
  useEffect(() => {
    if (!syncStatus?.inProgress) return undefined;
    const t = setTimeout(async () => {
      const next = await loadSyncStatus();
      // When the background sync finishes, pull the new data into the app
      if (next && !next.inProgress) {
        fetchTransactions?.({ silent: true });
        fetchAccounts?.();
      }
    }, 5000);
    return () => clearTimeout(t);
  }, [syncStatus, loadSyncStatus, fetchTransactions, fetchAccounts]);

  // Persist page size, mirroring the Transactions page behaviour
  useEffect(() => {
    localStorage.setItem('import_rowsPerPage', String(rowsPerPage));
  }, [rowsPerPage]);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Toggle expanded method (accordion behavior)
  const toggleMethod = (method) => {
    if (expandedMethod === method) {
      setExpandedMethod(null);
    } else {
      setExpandedMethod(method);
      resetState();
    }
  };

  // Run a full sync (fetch + save) right now — the same server-side pipeline
  // the webhook-triggered auto-import uses.
  const handleSyncNow = async () => {
    setSyncing(true);
    setResult(null);
    try {
      // Ask Akahu to re-pull from the banks first (best effort — the sync
      // imports whatever Akahu already has either way, and anything that
      // lands later arrives automatically via webhooks).
      await api.post('/akahu/refresh').catch(() => {});
      const res = await api.post('/akahu/sync');
      if (res.status === 202 || res.data?.inProgress) {
        setResult({ success: true, message: 'A sync is already running. New transactions will appear shortly.' });
      } else {
        const { inserted = 0, updated = 0, failed = 0 } = res.data || {};
        // Pull the fresh data into the app and refresh the sync status
        // BEFORE announcing completion, so "Sync complete" never shows
        // while the buttons still say "Syncing…".
        await Promise.all([
          fetchTransactions?.({ silent: true }),
          fetchAccounts?.()
        ]);
        await loadSyncStatus();
        setResult({
          success: true,
          message: `Sync complete: ${inserted} new, ${updated} updated${failed ? `, ${failed} failed` : ''}.`
        });
      }
    } catch (error) {
      setResult({ success: false, message: error.response?.data?.error || `Sync failed: ${error.message}` });
    } finally {
      setSyncing(false);
      loadSyncStatus();
    }
  };

  // Start elapsed time timer
  const startElapsedTimer = () => {
    importStartTimeRef.current = Date.now();
    setElapsedTime(0);
    setImportStats({ new: 0, updated: 0, failed: 0 });
    elapsedTimerRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - importStartTimeRef.current) / 1000));
    }, 1000);
  };

  // Stop elapsed time timer
  const stopElapsedTimer = () => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  };

  // Format elapsed time as mm:ss
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const newFile = e.target.files[0];
      setFile(newFile);
      resetState();
      setPage(1);
  
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const lines = text.split('\n');
  
          if (lines.length > 0) {
            const fileHeaders = lines[0].split(',').map((h) => h.trim());
            setHeaders(fileHeaders);
  
            const newMappings = { ...mappings };
            fileHeaders.forEach((header, index) => {
              const lowerHeader = header.toLowerCase();
              if (lowerHeader.includes('date')) newMappings.date = index;
              else if (lowerHeader.includes('desc')) newMappings.description = index;
              else if (lowerHeader.includes('amount')) newMappings.amount = index;
              else if (lowerHeader.includes('account')) newMappings.account = index;
              else if (lowerHeader.includes('balance')) newMappings.balance = index;
            });
            setMappings(newMappings);
  
            const previewRows = [];
            for (let i = 1; i < lines.length; i++) {
              if (lines[i].trim()) {
                const values = lines[i].split(',').map((v) => v.trim());
                previewRows.push(values);
              }
            }
            setPreview(previewRows);
          }
        } catch (error) {
          setResult({ success: false, message: `Error reading CSV file: ${error.message}` });
        }
      };
      reader.readAsText(newFile);
    }
  };

  const handleMappingChange = (field, value) => {
    setMappings({ ...mappings, [field]: value });
  };

  const readFileAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });

  const parseCSVWithMappings = (text) => {
    const lines = text.split('\n');
    return lines.slice(1)
      .filter(line => line.trim())
      .map((line, index) => {
        const values = line.split(',').map(v => v.trim());
        
        const date = values[mappings.date] || '';
        const desc = values[mappings.description] || '';
        const amount = values[mappings.amount] ? parseFloat(values[mappings.amount]) : 0;
        
        const stableData = `${date}${desc}${amount}`;
        const hashCode = Array.from(stableData)
          .reduce((s, c) => Math.imul(31, s) + c.charCodeAt(0) | 0, 0)
          .toString(16);
        
        return {
          id: `csv_${hashCode}`,
          date: values[mappings.date] || '',
          description: values[mappings.description] || '',
          amount: values[mappings.amount] ? parseFloat(values[mappings.amount]) : 0,
          account: values[mappings.account] || (accounts[0]?.id || ''),
          balance: values[mappings.balance] ? parseFloat(values[mappings.balance]) : 0,
          category: 'uncategorized'
        };
      });
  };

  const validateTransactions = (transactions) => {
    const missingBalances = transactions.filter(tx => 
      !tx.isPending && (tx.balance === undefined || tx.balance === null || isNaN(tx.balance))
    );
    
    const invalidDates = transactions.filter(tx => {
      const date = new Date(tx.date);
      return isNaN(date.getTime());
    });
    
    const invalidAmounts = transactions.filter(tx => 
      isNaN(tx.amount) || tx.amount === undefined || tx.amount === null
    );
    
    return {
      valid: missingBalances.length === 0 && invalidDates.length === 0 && invalidAmounts.length === 0,
      missingBalances,
      invalidDates,
      invalidAmounts
    };
  };

  const handleImportCSV = async () => {
    if (!file) return;
    
    const latestAccounts = typeof fetchAccounts === 'function' ? await fetchAccounts() : accounts;
    
    try {
      setLoading(true);
      setShowProgressDialog(true);
      startElapsedTimer();
      setProgress({ current: 0, total: 100, percentage: 0, stage: 'Reading CSV file...', step: 1 });
      
      const text = await readFileAsText(file);
      setProgress({ current: 25, total: 100, percentage: 25, stage: 'Parsing transactions...', step: 2 });
      
      const csvTransactions = parseCSVWithMappings(text);
      setProgress({ current: 50, total: 100, percentage: 50, stage: 'Validating transactions...', step: 3 });
      
      const validation = validateTransactions(csvTransactions);
      if (!validation.valid) {
        setResult({
          success: false,
          message: `Validation failed:\n${validation.missingBalances.length} missing balances\n${validation.invalidDates.length} invalid dates\n${validation.invalidAmounts.length} invalid amounts`
        });
        stopElapsedTimer();
        return;
      }

      // Check for unknown accounts before importing
      const { unknownAccounts, resolvedTransactions, autoResolved } = detectUnknownAccounts(csvTransactions, latestAccounts);
      if (unknownAccounts.length > 0) {
        // Pause import — show the account mapping dialog
        setLoading(false);
        setShowProgressDialog(false);
        stopElapsedTimer();
        setUnknownAccountsList(unknownAccounts);
        setAutoResolvedCount(autoResolved);
        setPendingImportData({ transactions: resolvedTransactions, latestAccounts, source: 'csv' });
        setShowAccountMappingDialog(true);
        return;
      }
      
      setProgress({ current: 75, total: 100, percentage: 75, stage: 'Importing transactions...', step: 4 });
      
      const progressCallback = (done, total) => {
        const percentage = total > 0 ? (done / total) * 100 : 0;
        setProgress({ 
          current: done, 
          total, 
          percentage: 75 + (percentage * 0.25), 
          stage: `Importing ${done} of ${total} transactions...`,
          step: 4
        });
      };
      
      const result = await importTransactionsFromCSV(resolvedTransactions, progressCallback, latestAccounts);
      
      setProgress({ current: 100, total: 100, percentage: 100, stage: 'Import completed!', step: 5 });
      stopElapsedTimer();
      setResult({ success: true, message: `Successfully imported ${result} transactions!` });
      
      setTimeout(() => {
        setShowProgressDialog(false);
        resetState();
      }, 2000);
      
    } catch (error) {
      stopElapsedTimer();
      setResult({ success: false, message: `Import failed: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const cancelOperation = () => {
    stopElapsedTimer();
    setLoading(false);
    setShowProgressDialog(false);
  };

  // Handler for when user confirms mappings in the Account Mapping Dialog
  const handleAccountMappingConfirm = async (mappingDecisions) => {
    setShowAccountMappingDialog(false);
    const { transactions, latestAccounts } = pendingImportData;

    const resolvedTxns = await applyAccountMappings(transactions, mappingDecisions, latestAccounts);
    // Refresh accounts after potential creation
    const refreshedAccounts = typeof fetchAccounts === 'function' ? await fetchAccounts() : accounts;

    // Resume the CSV import
    try {
      setLoading(true);
      setShowProgressDialog(true);
      startElapsedTimer();
      setProgress({ current: 75, total: 100, percentage: 75, stage: 'Importing transactions...', step: 4 });

      const progressCallback = (done, total) => {
        const percentage = total > 0 ? (done / total) * 100 : 0;
        setProgress({
          current: done,
          total,
          percentage: 75 + (percentage * 0.25),
          stage: `Importing ${done} of ${total} transactions...`,
          step: 4
        });
      };

      const result = await importTransactionsFromCSV(resolvedTxns, progressCallback, refreshedAccounts);

      setProgress({ current: 100, total: 100, percentage: 100, stage: 'Import completed!', step: 5 });
      stopElapsedTimer();
      setResult({ success: true, message: `Successfully imported ${result} transactions!` });

      setTimeout(() => {
        setShowProgressDialog(false);
        resetState();
      }, 2000);
    } catch (error) {
      stopElapsedTimer();
      setResult({ success: false, message: `Import failed: ${error.message}` });
    } finally {
      setLoading(false);
    }

    setPendingImportData(null);
    setUnknownAccountsList([]);
    setAutoResolvedCount(0);
  };

  const handleAccountMappingCancel = () => {
    setShowAccountMappingDialog(false);
    setPendingImportData(null);
    setUnknownAccountsList([]);
    setAutoResolvedCount(0);
  };

  const getPaginatedData = () => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return preview.slice(startIndex, endIndex);
  };

  const renderCSVImport = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a CSV file with your transactions. Select how the columns map to transaction fields.
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Balance field is required for all transactions to ensure accurate account balance tracking.
          </Typography>
        </Alert>
      </Box>

      <input type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} id="import-file" />
      <label htmlFor="import-file">
        <ActionButton 
          variant="outlined" 
          component="span"
          startIcon={<UploadFileIcon />}
          fullWidth={isMobile}
        >
          Select CSV File
        </ActionButton>
      </label>

      {file && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="500">
            Selected: {file.name}
          </Typography>
        </Box>
      )}

      {headers.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>Map CSV Columns to Transaction Fields</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {Object.keys(mappings).map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field}>
                <FormControl fullWidth size="small">
                  <InputLabel>{field.charAt(0).toUpperCase() + field.slice(1)}</InputLabel>
                  <Select
                    value={mappings[field]}
                    onChange={(e) => handleMappingChange(field, e.target.value)}
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {headers.map((header, index) => (
                      <MenuItem key={index} value={index}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" gutterBottom>Preview</Typography>
          
          {preview.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2">Rows per page:</Typography>
                <TextField
                  select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  size="small"
                  sx={{ width: '80px' }}
                  SelectProps={{ native: true }}
                >
                  {ROWS_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </TextField>
                <Typography variant="body2">
                  Showing {Math.min((page - 1) * rowsPerPage + 1, preview.length)} - {Math.min(page * rowsPerPage, preview.length)} of {preview.length} rows
                </Typography>
              </Box>
            </Box>
          )}
          
          <TableContainer component={Paper} sx={{ mb: 2, maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headers.map((header, index) => {
                    const mappedField = Object.keys(mappings).find(k => mappings[k] === index.toString());
                    return (
                      <TableCell 
                        key={index}
                        sx={mappedField === 'balance' ? { 
                          backgroundColor: 'rgba(255, 0, 0, 0.1)',
                          fontWeight: 'bold'
                        } : {}}
                      >
                        {header}
                        {mappedField && 
                          <Chip 
                            size="small" 
                            label={mappedField.charAt(0).toUpperCase() + mappedField.slice(1)} 
                            sx={{ ml: 1 }}
                            color={mappedField === 'balance' ? 'error' : 'primary'}
                          />
                        }
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {getPaginatedData().map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <TableCell 
                        key={cellIndex}
                        sx={Object.keys(mappings).find(k => mappings[k] === cellIndex.toString()) === 'balance' ? { 
                          backgroundColor: 'rgba(255, 0, 0, 0.05)',
                          fontWeight: 'bold'
                        } : {}}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={preview.length}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }}
            rowsPerPageOptions={ROWS_OPTIONS}
            sx={{
              '.MuiTablePagination-toolbar': {
                justifyContent: 'space-between',
                pl: 2,
              },
              '.MuiTablePagination-spacer': {
                display: 'none',
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-select, .MuiTablePagination-selectIcon': {
                order: 1,
              },
              '.MuiTablePagination-displayedRows': {
                order: 2,
                ml: 'auto',
              },
              '.MuiTablePagination-actions': {
                order: 3,
                ml: 1,
              },
            }}
          />
          
          <ActionButton 
            variant="contained" 
            onClick={handleImportCSV} 
            disabled={!mappings.date || !mappings.description || !mappings.amount || !mappings.balance}
            startIcon={<CloudUploadIcon />}
            fullWidth={isMobile}
          >
            Import Transactions
          </ActionButton>
        </Box>
      )}
    </Box>
  );


  // Bank sync panel: transactions import automatically via webhooks; this
  // panel shows the latest sync outcome and offers an immediate manual run.
  const renderAPIImport = () => {
    const lastSync = syncStatus?.lastSync;
    const busy = syncing || Boolean(syncStatus?.inProgress);
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Transactions from your connected bank accounts import and save automatically
          whenever your bank reports new activity. Use Sync Now to run an import immediately.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ActionButton
            variant="contained"
            onClick={handleSyncNow}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          >
            {busy ? 'Syncing…' : 'Sync Now'}
          </ActionButton>

          {busy ? (
            <Typography variant="body2" color="text.secondary">
              Importing the latest transactions…
            </Typography>
          ) : lastSync ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ScheduleIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Last synced {timeAgo(lastSync.completedAt)}
                {lastSync.status === 'completed'
                  ? `: ${lastSync.inserted || 0} new, ${lastSync.updated || 0} updated`
                  : lastSync.status === 'failed'
                    ? `, failed${lastSync.errorMessage ? ` (${lastSync.errorMessage})` : ''}`
                    : ''}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No syncs yet. Connect your bank in Settings, or run one now.
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  // Progress dialog steps (CSV import is the only flow that uses the dialog now)
  const currentSteps = [
    { label: 'Reading', key: 'reading' },
    { label: 'Parsing', key: 'parsing' },
    { label: 'Validating', key: 'validating' },
    { label: 'Importing', key: 'importing' },
    { label: 'Complete', key: 'complete' }
  ];

  const lastCompletedSync = syncStatus?.lastSync?.status === 'completed' ? syncStatus.lastSync : null;
  const lastSyncTotal = lastCompletedSync ? (lastCompletedSync.inserted || 0) + (lastCompletedSync.updated || 0) : 0;

  return (
      <Box sx={{ width: '100%' }}>
        <SectionHeader
          title="Import"
          icon={<CloudUploadIcon fontSize="large" />}
          sx={{ mb: 0 }}
        />

        <Box>
          {/* Recent Sync Summary — the latest automatic/manual bank sync */}
          {lastCompletedSync && (
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: '0 0 12px 12px',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                mb: 2,
                boxShadow: '0 4px 20px rgba(233, 30, 99, 0.08)',
                bgcolor: '#fce4ec', // Light pink background
                gap: { xs: 2, sm: 0 },
              }}
            >
              {/* Progress Circle and Info */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 2 },
                  flex: 1,
                }}
              >
                <Box sx={{ position: 'relative', display: 'inline-flex', mr: { xs: 1, sm: 2 } }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={60}
                    thickness={5}
                    sx={{
                      color: 'primary.main',
                      background: 'rgba(255,255,255,0.5)',
                      borderRadius: '50%',
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{
                        color: 'primary.main',
                        fontSize: lastSyncTotal > 999 ? '0.9rem' : '1.2rem',
                      }}
                    >
                      {lastSyncTotal}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{
                      color: 'primary.main',
                      fontSize: { xs: '1rem', sm: '1.15rem' },
                    }}
                  >
                    Recent Sync
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.dark',
                      fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    }}
                  >
                    {new Date(lastCompletedSync.completedAt).toLocaleDateString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })} • {lastCompletedSync.inserted || 0} new, {lastCompletedSync.updated || 0} updated
                  </Typography>
                </Box>
              </Box>

              {/* Action Button */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  mt: { xs: 1, sm: 0 },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <ActionButton
                  variant="contained"
                  onClick={handleSyncNow}
                  startIcon={syncing || syncStatus?.inProgress ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  disabled={loading || syncing || Boolean(syncStatus?.inProgress)}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {syncing || syncStatus?.inProgress ? 'Syncing…' : 'Sync Now'}
                </ActionButton>
              </Box>
            </Paper>
          )}

          {/* Fetch/save outcome — surfaced above the cards so it's visible
              without scrolling */}
          {result && (
            <Alert
              severity={result.success ? 'success' : 'error'}
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => setResult(null)}>
                  Dismiss
                </Button>
              }
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {result.message}
              </Typography>
            </Alert>
          )}

          {/* API Import Card (Collapsible) */}
          <Card
            sx={{
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
            }}
          >
            {/* Card Header - Always visible, clickable. Styled to match the
                AppCard header used on the other pages. */}
            <Box
              onClick={() => toggleMethod('api')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                py: 0.5,
                minHeight: 56,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
                transition: 'background-color 0.2s',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
              >
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}
                >
                  <SyncIcon />
                </Box>
                Bank Sync
              </Typography>
              <IconButton size="small" sx={{ color: 'grey.500' }}>
                {expandedMethod === 'api' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* Card Content - Collapsible */}
            {expandedMethod === 'api' && (
              <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                {renderAPIImport()}
              </Box>
            )}
          </Card>

          {/* CSV Import Card (Collapsible) */}
          <Card 
            sx={{ 
              mb: 2, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 2,
              transition: 'all 0.2s ease',
            }}
          >
            {/* Card Header - Always visible, clickable. Styled to match the
                AppCard header used on the other pages. */}
            <Box
              onClick={() => toggleMethod('csv')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                py: 0.5,
                minHeight: 56,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
                transition: 'background-color 0.2s',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
              >
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}
                >
                  <UploadFileIcon />
                </Box>
                Import from CSV
              </Typography>
              <IconButton size="small" sx={{ color: 'grey.500' }}>
                {expandedMethod === 'csv' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            {/* Card Content - Collapsible */}
            {expandedMethod === 'csv' && (
              <Box sx={{ p: { xs: 2, sm: 3 }, pt: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                {renderCSVImport()}
              </Box>
            )}
          </Card>

          {loading && <LinearProgress sx={{ mt: 2 }} />}


          {/* Import Progress Dialog — built on the shared AppDialog shell so it
              matches the rest of the app (clean header, paper background, stacked footer) */}
          <AppDialog
            open={showProgressDialog}
            onClose={loading ? undefined : () => setShowProgressDialog(false)}
            title="Import Progress"
            primaryLabel={loading ? 'Cancel' : 'Done'}
            primaryColor={loading ? 'error' : 'primary'}
            onPrimary={loading ? cancelOperation : () => setShowProgressDialog(false)}
            secondaryLabel={null}
          >
              {/* Step Indicators - Dot style */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
                {currentSteps.map((step, index) => {
                  const stepNum = index + 1;
                  const isComplete = progress.step > stepNum;
                  const isCurrent = progress.step === stepNum;
                  return (
                    <Box key={step.key} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%',
                            bgcolor: isComplete ? 'success.main' : isCurrent ? 'primary.main' : 'grey.300',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            boxShadow: isCurrent ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}` : 'none'
                          }}
                        >
                          {isComplete ? (
                            <CheckIcon sx={{ color: 'white', fontSize: 14 }} />
                          ) : isCurrent ? (
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'white' }} />
                          ) : null}
                        </Box>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            mt: 0.5, 
                            color: isCurrent ? 'primary.main' : isComplete ? 'success.main' : 'text.disabled',
                            fontWeight: isCurrent ? 600 : 400,
                            fontSize: '0.75rem'
                          }}
                        >
                          {step.label}
                        </Typography>
                      </Box>
                      {index < currentSteps.length - 1 && (
                        <Box 
                          sx={{ 
                            width: 20, 
                            height: 2, 
                            bgcolor: isComplete ? 'success.main' : 'grey.300',
                            mx: 0.5,
                            mb: 2,
                            transition: 'background-color 0.3s ease'
                          }} 
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'text.primary' }}>
                  {progress.stage || 'Processing…'}
                </Typography>

                <Box sx={{ position: 'relative', my: 3, mx: 'auto', width: 110, height: 110 }}>
                  <CircularProgress
                    variant={progress.indeterminate ? 'indeterminate' : 'determinate'}
                    value={progress.indeterminate ? undefined : progress.percentage}
                    size={110}
                    thickness={5}
                    sx={{ color: 'primary.main', bgcolor: 'background.paper', borderRadius: '50%' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {progress.indeterminate ? (
                      progress.isFetching && progress.current > 0 ? (
                        <>
                          <Typography variant="h5" component="div" color="primary" fontWeight={700}>
                            {progress.current.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">fetched</Typography>
                        </>
                      ) : (
                        <SyncIcon color="primary" sx={{ fontSize: 30 }} />
                      )
                    ) : (
                      <Typography variant="h5" component="div" color="primary" fontWeight={700}>
                        {`${Math.round(progress.percentage)}%`}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Elapsed Time */}
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatElapsedTime(elapsedTime)}
                    </Typography>
                  </Box>
                )}

                {/* Live Stats — real new/updated/failed counts from the save result */}
                {(importStats.new > 0 || importStats.updated > 0 || importStats.failed > 0) && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 1 }}>
                    {importStats.new > 0 && (
                      <Chip
                        label={`${importStats.new} new`}
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: 'success.main' }}
                      />
                    )}
                    {importStats.updated > 0 && (
                      <Chip
                        label={`${importStats.updated} updated`}
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.info.main, 0.12), color: 'info.main' }}
                      />
                    )}
                    {importStats.failed > 0 && (
                      <Chip
                        label={`${importStats.failed} failed`}
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.error.main, 0.12), color: 'error.main' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </AppDialog>

          {/* Account Mapping Dialog */}
          <AccountMappingDialog
            open={showAccountMappingDialog}
            onClose={handleAccountMappingCancel}
            onConfirm={handleAccountMappingConfirm}
            unknownAccounts={unknownAccountsList}
            existingAccounts={accounts}
            autoResolvedCount={autoResolvedCount}
          />
        </Box>
      </Box>
  );
};

export default CsvImportTool;